// ---------------------------------------------------------------------------------------------
// Billing and invoicing
//
// Every money value crosses the wire as a fixed two-place string, never a number. A JSON number
// is a double, and a double cannot hold 0.1 exactly — serialising currency through one is how
// totals drift by a paisa between the server and the screen.
// ---------------------------------------------------------------------------------------------
export const INVOICE_STATUS = {
    DRAFT: 'DRAFT',
    ISSUED: 'ISSUED',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    PAID: 'PAID',
    OVERDUE: 'OVERDUE',
    CANCELLED: 'CANCELLED',
    VOID: 'VOID',
};
export const INVOICE_STATUS_LABELS = {
    DRAFT: 'Draft',
    ISSUED: 'Issued',
    PARTIALLY_PAID: 'Partially paid',
    PAID: 'Paid',
    OVERDUE: 'Overdue',
    CANCELLED: 'Cancelled',
    VOID: 'Void',
};
/** Statuses a client may see in the portal. A draft is not a document anyone has been sent. */
export const CLIENT_VISIBLE_INVOICE_STATUSES = [
    'ISSUED',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'VOID',
];
/**
 * Statuses that mean "this invoice is closed, stop treating it as owing".
 *
 * One definition because three surfaces answer with it and a client sees two of them: the internal
 * list's `isOverdue` flag, the portal's, and the nightly sweep that decides whether to email a
 * client that they are late. Drift between them puts a screen and an email in contradiction —
 * an invoice shown as settled while the sweep still chases it.
 *
 * `VOID` is deliberately both settled *and* client-visible: a voided invoice stops owing money,
 * but the client keeps the document. That is why this set and `CLIENT_VISIBLE_INVOICE_STATUSES`
 * cannot be merged — they answer different questions about the same status.
 */
export const SETTLED_INVOICE_STATUSES = ['PAID', 'CANCELLED', 'VOID'];
/** Whether an invoice has stopped owing money, whatever its balance says. */
export function isSettledInvoiceStatus(status) {
    return SETTLED_INVOICE_STATUSES.includes(status);
}
/** Statuses that still owe money. */
export const OPEN_INVOICE_STATUSES = [
    'ISSUED',
    'PARTIALLY_PAID',
    'OVERDUE',
];
/**
 * Which transitions the API allows.
 *
 * A paid invoice is terminal: correcting one is a credit note, not an edit. Void is reachable
 * from every issued state because a document that has left the building can only be withdrawn,
 * never deleted — the number stays in the sequence.
 */
export const INVOICE_TRANSITIONS = {
    DRAFT: ['ISSUED', 'CANCELLED'],
    ISSUED: ['PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED', 'VOID'],
    PARTIALLY_PAID: ['PAID', 'OVERDUE', 'VOID'],
    OVERDUE: ['PARTIALLY_PAID', 'PAID', 'VOID'],
    PAID: ['VOID'],
    CANCELLED: [],
    VOID: [],
};
export function canTransitionInvoice(from, to) {
    return INVOICE_TRANSITIONS[from].includes(to);
}
/** Only a draft may be edited. Anything issued is a financial document. */
export function isInvoiceEditable(status) {
    return status === 'DRAFT';
}
export const TAX_TREATMENT = {
    EXCLUSIVE: 'EXCLUSIVE',
    INCLUSIVE: 'INCLUSIVE',
};
export const TAX_TREATMENT_LABELS = {
    EXCLUSIVE: 'Prices exclude GST',
    INCLUSIVE: 'Prices include GST',
};
export const SUPPLY_TYPE = {
    INTRA_STATE: 'INTRA_STATE',
    INTER_STATE: 'INTER_STATE',
    EXPORT: 'EXPORT',
    EXEMPT: 'EXEMPT',
};
export const SUPPLY_TYPE_LABELS = {
    INTRA_STATE: 'Within the state (CGST + SGST)',
    INTER_STATE: 'Between states (IGST)',
    EXPORT: 'Export (no GST)',
    EXEMPT: 'Exempt (no GST)',
};
export const PAYMENT_METHOD = {
    BANK_TRANSFER: 'BANK_TRANSFER',
    UPI: 'UPI',
    CHEQUE: 'CHEQUE',
    CARD: 'CARD',
    CASH: 'CASH',
    OTHER: 'OTHER',
};
export const PAYMENT_METHOD_LABELS = {
    BANK_TRANSFER: 'Bank transfer',
    UPI: 'UPI',
    CHEQUE: 'Cheque',
    CARD: 'Card',
    CASH: 'Cash',
    OTHER: 'Other',
};
export const INVOICE_LINE_SOURCE = {
    MANUAL: 'MANUAL',
    MILESTONE: 'MILESTONE',
    SUPPORT_HOURS: 'SUPPORT_HOURS',
    CHANGE_REQUEST: 'CHANGE_REQUEST',
    CONTRACT: 'CONTRACT',
};
//# sourceMappingURL=billing.js.map